import moment from 'moment';
import { getMomentLocale } from '../i18n/locales.js';
import { getRuntimeLocale, translateRuntimeKey } from '../i18n/runtimeState.js';

export function getLocalizedTodayDate(locale) {
    return (
        moment()
            .locale(getMomentLocale(locale))
            // LL keeps natural date order in French/English
            .format('dddd, LL')
    );
}

export const DATE_RANGE_PRESET_KEYS = Object.freeze({
    LAST_7_DAYS: 'last-7-days',
    LAST_30_DAYS: 'last-30-days',
    LAST_90_DAYS: 'last-90-days',
    PREV_WEEK: 'prev-week',
    PREV_MONTH: 'prev-month',
    PREV_QUARTER: 'prev-quarter',
    THIS_WEEK: 'this-week',
    THIS_MONTH: 'this-month',
    THIS_QUARTER: 'this-quarter',
    THIS_YEAR: 'this-year',
    TODAY: 'today',
});

const DATE_RANGE_PRESET_TRANSLATION_KEYS = Object.freeze({
    [DATE_RANGE_PRESET_KEYS.TODAY]: 'fields.dateOrDateRange.presets.today',
    [DATE_RANGE_PRESET_KEYS.LAST_7_DAYS]:
        'fields.dateOrDateRange.presets.last7Days',
    [DATE_RANGE_PRESET_KEYS.LAST_30_DAYS]:
        'fields.dateOrDateRange.presets.last30Days',
    [DATE_RANGE_PRESET_KEYS.LAST_90_DAYS]:
        'fields.dateOrDateRange.presets.last90Days',
    [DATE_RANGE_PRESET_KEYS.PREV_WEEK]:
        'fields.dateOrDateRange.presets.prevWeek',
    [DATE_RANGE_PRESET_KEYS.PREV_MONTH]:
        'fields.dateOrDateRange.presets.prevMonth',
    [DATE_RANGE_PRESET_KEYS.PREV_QUARTER]:
        'fields.dateOrDateRange.presets.prevQuarter',
    [DATE_RANGE_PRESET_KEYS.THIS_WEEK]:
        'fields.dateOrDateRange.presets.thisWeek',
    [DATE_RANGE_PRESET_KEYS.THIS_MONTH]:
        'fields.dateOrDateRange.presets.thisMonth',
    [DATE_RANGE_PRESET_KEYS.THIS_QUARTER]:
        'fields.dateOrDateRange.presets.thisQuarter',
    [DATE_RANGE_PRESET_KEYS.THIS_YEAR]:
        'fields.dateOrDateRange.presets.thisYear',
});

/**
 * @returns {Array<{ key: string, label: string, resolve: () => [moment.Moment, moment.Moment], slot: string }>}
 */
function translatePresetLabel(translateFn, key, fallback) {
    const resolvedTranslateFn =
        typeof translateFn === 'function'
            ? translateFn
            : translateDefaultDateRangePresetLabel;

    const translated = resolvedTranslateFn(key, fallback);
    return translated && translated !== key ? translated : fallback;
}

function translateDefaultDateRangePresetLabel(key, fallback) {
    const translationKey = DATE_RANGE_PRESET_TRANSLATION_KEYS[key];
    if (!translationKey) return fallback;

    const translated = translateRuntimeKey(translationKey);
    return translated === translationKey ? fallback : translated;
}

function activeLocale() {
    return getRuntimeLocale();
}

function displayDateFormatForLocale(locale) {
    return locale === 'fr' ? 'D MMM YYYY' : 'MMM D, YYYY';
}

export function getDateRangePresetDefinitions(translateFn = null) {
    const now = moment();
    const previousWeek = now.clone().subtract(1, 'week');
    const previousMonth = now.clone().subtract(1, 'month');
    const previousQuarter = now.clone().subtract(1, 'quarter');

    return [
        {
            key: DATE_RANGE_PRESET_KEYS.TODAY,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.TODAY,
                'Today'
            ),
            resolve: () => [now.clone(), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
                'Last 7 Days'
            ),
            resolve: () => [now.clone().subtract(6, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
                'Last 30 Days'
            ),
            resolve: () => [now.clone().subtract(29, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
                'Last 90 Days'
            ),
            resolve: () => [now.clone().subtract(89, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_WEEK,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.PREV_WEEK,
                'Prev. Week'
            ),
            resolve: () => [
                previousWeek.clone().startOf('isoWeek'),
                previousWeek.clone().endOf('isoWeek'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_MONTH,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.PREV_MONTH,
                'Prev. Month'
            ),
            resolve: () => [
                previousMonth.clone().startOf('month'),
                previousMonth.clone().endOf('month'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
                'Prev. Quarter'
            ),
            resolve: () => [
                previousQuarter.clone().startOf('quarter'),
                previousQuarter.clone().endOf('quarter'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_WEEK,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.THIS_WEEK,
                'This Week'
            ),
            resolve: () => [
                now.clone().startOf('isoWeek'),
                now.clone().endOf('isoWeek'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_MONTH,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.THIS_MONTH,
                'This Month'
            ),
            resolve: () => [
                now.clone().startOf('month'),
                now.clone().endOf('month'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
                'This Quarter'
            ),
            resolve: () => [
                now.clone().startOf('quarter'),
                now.clone().endOf('quarter'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_YEAR,
            label: translatePresetLabel(
                translateFn,
                DATE_RANGE_PRESET_KEYS.THIS_YEAR,
                'This Year'
            ),
            resolve: () => [
                now.clone().startOf('year'),
                now.clone().endOf('year'),
            ],
            slot: 'preset-date-range-button',
        },
    ];
}

/**
 * @param {string} key
 * @returns {{ key: string, label: string, resolve: Function }|undefined}
 */
export function getDateRangePresetDefinitionByKey(key, translateFn = null) {
    if (!key) return undefined;
    return getDateRangePresetDefinitions(translateFn).find(
        (preset) => preset.key === String(key)
    );
}

/**
 * @param {string} key
 * @returns {[string, string]|null}
 */
export function resolveDateRangeFromPresetKey(key) {
    const definition = getDateRangePresetDefinitionByKey(key);
    if (!definition) return null;

    const [start, end] = definition.resolve();
    return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string|null}
 */
export function getPresetKeyForRange(startDate, endDate) {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (!startMoment.isValid() || !endMoment.isValid()) {
        return null;
    }

    const targetStart = startMoment.clone().startOf('day');
    const targetEnd = endMoment.clone().startOf('day');

    const matched = getDateRangePresetDefinitions().find((preset) => {
        const [presetStart, presetEnd] = preset.resolve();
        return (
            presetStart.clone().startOf('day').isSame(targetStart, 'day') &&
            presetEnd.clone().startOf('day').isSame(targetEnd, 'day')
        );
    });

    return matched?.key ?? null;
}

/**
 * @param {string} key
 * @returns {string}
 */
export function getPresetLabelByKey(key, translateFn = null) {
    return getDateRangePresetDefinitionByKey(key, translateFn)?.label ?? '';
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string}
 */
export function getPresetLabelForRange(startDate, endDate, translateFn = null) {
    const key = getPresetKeyForRange(startDate, endDate);
    return key ? getPresetLabelByKey(key, translateFn) : '';
}

/**
 * Title-friendly label for a date range (e.g. "August", "Q2", "2026" for calendar presets).
 * @param {string} startDate
 * @param {string} endDate
 * @param {string|null} [presetKey]
 * @returns {string}
 */
export function getDateRangeTitleLabel(
    startDate,
    endDate,
    presetKey = null,
    translateFn = null
) {
    const key =
        presetKey && getDateRangePresetDefinitionByKey(presetKey)
            ? String(presetKey)
            : getPresetKeyForRange(startDate, endDate);
    const presetLabel = key ? getPresetLabelByKey(key, translateFn) : '';

    if (!presetLabel) {
        return '';
    }

    const locale = activeLocale();
    const rangeMoment = moment(startDate, moment.ISO_8601, true).locale(
        getMomentLocale(locale)
    );
    if (!rangeMoment.isValid()) {
        return presetLabel;
    }

    switch (key) {
        case DATE_RANGE_PRESET_KEYS.TODAY:
            return '';
        case DATE_RANGE_PRESET_KEYS.THIS_MONTH:
        case DATE_RANGE_PRESET_KEYS.PREV_MONTH:
            return rangeMoment.format('MMMM');
        case DATE_RANGE_PRESET_KEYS.THIS_QUARTER:
        case DATE_RANGE_PRESET_KEYS.PREV_QUARTER:
            return `Q${rangeMoment.quarter()}`;
        case DATE_RANGE_PRESET_KEYS.THIS_YEAR:
            return rangeMoment.format('YYYY');
        default:
            return presetLabel;
    }
}

/** Preset keys shown per filter surface (picker UI only). */
export const DATE_RANGE_PRESET_KEYS_BY_CONTEXT = Object.freeze({
    'template-stats': [
        DATE_RANGE_PRESET_KEYS.TODAY,
        DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_WEEK,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
    ],
    performance: [
        DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
        DATE_RANGE_PRESET_KEYS.THIS_YEAR,
    ],
    default: [
        DATE_RANGE_PRESET_KEYS.TODAY,
        DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
        DATE_RANGE_PRESET_KEYS.THIS_YEAR,
    ],
});

function mapPresetDefinitionToPickerOption(preset) {
    return {
        key: preset.key,
        label: preset.label,
        value: preset.resolve().map((date) => date.toDate()),
        slot: preset.slot,
    };
}

/**
 * Date-picker preset buttons for a given surface.
 * @param {'template-stats'|'performance'|'default'} [context='default']
 * @returns {Array<{ key: string, label: string, value: Date[], slot: string }>}
 */
export function getDateRangePresetsForContext(
    context = 'default',
    translateFn = null
) {
    const allowedKeys =
        DATE_RANGE_PRESET_KEYS_BY_CONTEXT[context] ??
        DATE_RANGE_PRESET_KEYS_BY_CONTEXT.default;
    const definitionsByKey = new Map(
        getDateRangePresetDefinitions(translateFn).map((preset) => [
            preset.key,
            preset,
        ])
    );

    return allowedKeys
        .map((key) => definitionsByKey.get(key))
        .filter(Boolean)
        .map(mapPresetDefinitionToPickerOption);
}

/** @deprecated Prefer getDateRangePresetsForContext('default') */
export function getDefaultDateRangePresets() {
    return getDateRangePresetsForContext('default');
}

/**
 * @param {{ siteIds?: string[], areaIds?: string[], auditorIds?: string[], dateRange?: string[], dateRangePreset?: string|null }} filters
 * @returns {{ siteIds: string[], areaIds: string[], auditorIds: string[], dateRange?: string[], dateRangePreset?: string }}
 */
export function serializeDateFiltersForStorage(filters = {}) {
    const siteIds = Array.isArray(filters.siteIds) ? filters.siteIds : [];
    const areaIds = Array.isArray(filters.areaIds) ? filters.areaIds : [];
    const auditorIds = Array.isArray(filters.auditorIds)
        ? filters.auditorIds
        : [];
    const templateIds = Array.isArray(filters.templateIds)
        ? filters.templateIds
        : [];

    const presetKey =
        filters.dateRangePreset &&
        getDateRangePresetDefinitionByKey(filters.dateRangePreset)
            ? String(filters.dateRangePreset)
            : inferDateRangePresetKey(filters.dateRange);

    if (presetKey) {
        return {
            siteIds,
            areaIds,
            auditorIds,
            templateIds,
            dateRangePreset: presetKey,
        };
    }

    const dateRange = normalizeDateRangeStrings(filters.dateRange);
    return {
        siteIds,
        areaIds,
        auditorIds,
        templateIds,
        ...(dateRange.length >= 2 ? { dateRange } : {}),
    };
}

/**
 * @param {object} stored
 * @param {[string, string]} fallbackRange
 * @returns {{ siteIds: string[], areaIds: string[], auditorIds: string[], dateRange: string[], dateRangePreset: string|null }}
 */
export function deserializeDateFiltersFromStorage(
    stored = {},
    fallbackRange = []
) {
    const siteIds = Array.isArray(stored.siteIds) ? stored.siteIds : [];
    const areaIds = Array.isArray(stored.areaIds) ? stored.areaIds : [];
    const auditorIds = Array.isArray(stored.auditorIds)
        ? stored.auditorIds
        : [];
    const templateIds = Array.isArray(stored.templateIds)
        ? stored.templateIds
        : [];

    const fallback = normalizeDateRangeStrings(fallbackRange);
    const safeFallback = fallback.length >= 2 ? fallback : [];

    if (stored.dateRangePreset) {
        const resolved = resolveDateRangeFromPresetKey(stored.dateRangePreset);
        if (resolved) {
            return {
                siteIds,
                areaIds,
                auditorIds,
                templateIds,
                dateRange: resolved,
                dateRangePreset: String(stored.dateRangePreset),
            };
        }
    }

    const dateRange = normalizeDateRangeStrings(stored.dateRange);
    const resolvedRange = dateRange.length >= 2 ? dateRange : safeFallback;

    return {
        siteIds,
        areaIds,
        auditorIds,
        templateIds,
        dateRange: resolvedRange,
        dateRangePreset: null,
    };
}

/**
 * @param {string[]|null|undefined} dateRange
 * @returns {string|null}
 */
export function inferDateRangePresetKey(dateRange) {
    const normalized = normalizeDateRangeStrings(dateRange);
    if (normalized.length < 2) {
        return null;
    }

    return getPresetKeyForRange(normalized[0], normalized[1]);
}

/**
 * @param {string[]|null|undefined} values
 * @returns {string[]}
 */
export function normalizeDateRangeStrings(values) {
    if (!Array.isArray(values) || values.length < 2) {
        return [];
    }

    const [start, end] = values;
    const normalizedStart = String(start);
    const normalizedEnd = String(end);
    const startValid = moment(normalizedStart, 'YYYY-MM-DD', true).isValid();
    const endValid = moment(normalizedEnd, 'YYYY-MM-DD', true).isValid();

    if (!startValid || !endValid) {
        return [];
    }

    return [normalizedStart, normalizedEnd];
}

export function formatDisplayDate(isoString, locale = activeLocale()) {
    if (!isoString) return '';
    const date = moment
        .utc(isoString, moment.ISO_8601, true)
        .locale(getMomentLocale(locale));
    if (!date.isValid()) return '';

    return date.format(displayDateFormatForLocale(locale));
}

export function formatDisplayDateRange(
    startDate,
    endDate,
    locale = activeLocale()
) {
    const startLabel = formatDisplayDate(startDate, locale);
    const endLabel = formatDisplayDate(endDate, locale);

    if (!startLabel || !endLabel) return '';
    return `${startLabel} - ${endLabel}`;
}

export function formatLocalizedDate(isoString, locale, format = 'll') {
    if (!isoString) return '';
    const date = moment.utc(isoString).locale(getMomentLocale(locale));
    if (!date.isValid()) return '';

    return date.format(format);
}

export function formatLocalDateTime(isoString) {
    if (!isoString) return '';
    return moment.utc(isoString).local().format('MM/DD/YYYY hh:mm A');
}

export function formatLocalizedDateTime(isoString, locale) {
    if (!isoString) return '';
    const localMoment = moment(isoString).locale(getMomentLocale(locale));
    if (!localMoment.isValid()) return '';

    return `${localMoment.format('ll')} • ${localMoment
        .format('LT')
        .toLowerCase()}`;
}

/**
 * Convert a date-only value into a UTC day boundary ISO string.
 * @param {string|Date} dateValue
 * @param {'start'|'end'} boundary
 * @returns {string|null}
 */
function toUtcDayBoundaryIso(dateValue, boundary) {
    if (!dateValue) return null;

    const localMoment = moment(dateValue);
    if (!localMoment.isValid()) return null;

    const dateString = localMoment.format('YYYY-MM-DD');
    const utcMoment = moment.utc(dateString, 'YYYY-MM-DD', true);
    if (!utcMoment.isValid()) return null;

    return boundary === 'end'
        ? utcMoment.endOf('day').toISOString()
        : utcMoment.startOf('day').toISOString();
}

/**
 * Normalize a date range input into a start/end pair.
 * @param {string|Date|Array<string|Date>} value
 * @returns {{start: string|Date, end: string|Date}|null}
 */
export function normalizeDateRangeInput(value) {
    if (!value) return null;

    if (Array.isArray(value)) {
        const [start, end] = value;
        const rangeStart = start || end;
        const rangeEnd = end || start;
        return rangeStart ? { start: rangeStart, end: rangeEnd } : null;
    }

    return { start: value, end: value };
}

/**
 * Convert a date-only value into a UTC start-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayStartIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'start');
}

/**
 * Convert a date-only value into a UTC end-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayEndIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'end');
}
